import type { UsageExchangeRate, UsageSubject, UsageTier } from "@amoreg/contracts";
import { Building2Icon, RefreshCwIcon, UserIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { useActiveOrgId } from "../../state/orgs";
import { useModelSpend } from "../../state/modelSpend";
import { useUsage } from "../../state/usage";
import {
  enumerateDays,
  formatCount,
  formatDayShort,
  formatGbp,
  formatPercent,
  formatTokens,
  makeWindow,
} from "../../usage/usageFormat";
import { ScrollArea } from "../ui/scroll-area";
import { PaidUsageLimit } from "./PaidUsageLimit";
import { UsageCoverageNotice } from "./UsageCoverageNotice";
import {
  TierSwatch,
  UsageChartLegend,
  UsageTierChart,
  type UsageChartMetric,
} from "./UsageTierChart";
import { activeTiers, TIER_COLOR, TIER_LABEL } from "./usageTiers";

const WINDOW_OPTIONS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

/**
 * Whose calls the page shows. A person's own usage is the default: it is the
 * answer most members come for, and it is the narrower read. The organization
 * view is one click away for whoever is watching the whole tenant's spend.
 */
const SUBJECT_OPTIONS: ReadonlyArray<{
  readonly subject: UsageSubject;
  readonly label: string;
  readonly icon: typeof UserIcon;
}> = [
  { subject: "me", label: "You", icon: UserIcon },
  { subject: "organization", label: "Whole organization", icon: Building2Icon },
];

export function UsagePage() {
  const [windowDays, setWindowDays] = useState<number>(30);
  const [subject, setSubject] = useState<UsageSubject>("me");
  const [metric, setMetric] = useState<UsageChartMetric>("cost");
  const [breakdown, setBreakdown] = useState<"tier" | "day" | "agent">("tier");

  // The tenant the workspace is showing. The server re-checks it against the
  // session's memberships, so this only ever narrows what is returned.
  const organizationId = useActiveOrgId();
  // Recomputed only when the window length or tenant changes, so a re-render
  // does not shift the range and refetch every environment.
  const window = useMemo(
    () => makeWindow(windowDays, organizationId, subject),
    [organizationId, subject, windowDays],
  );
  const { merged, environments, exchangeRate, coverageNotices, isPending, isPartial, refresh } =
    useUsage(window);
  // The allowance meter reads the same organization; one Refresh control
  // must refresh the money as well as the charts.
  const { refresh: refreshSpend } = useModelSpend(organizationId);
  const refreshAll = useCallback(() => {
    refresh();
    refreshSpend();
  }, [refresh, refreshSpend]);

  const days = useMemo(
    () => enumerateDays(window.sinceDay, window.untilDay),
    [window.sinceDay, window.untilDay],
  );
  const recentDays = useMemo(() => merged.daily.toReversed().slice(0, 8), [merged.daily]);

  // Ranked by whatever the toggle is showing, so the bars always descend.
  const orderedTiers = useMemo(
    () =>
      merged.tiers.toSorted((a, b) =>
        metric === "cost" ? b.costGbp - a.costGbp : b.totalTokens - a.totalTokens,
      ),
    [merged.tiers, metric],
  );
  // Only the tiers that served something: an empty column, legend entry and
  // hover row for a tier this tenant has never used answers nothing.
  const tiers = useMemo(() => activeTiers(merged.tiers.map((tier) => tier.tier)), [merged.tiers]);

  const activeDays = merged.daily.filter((day) => day.totalTokens > 0).length;
  const dailyAverage = activeDays === 0 ? 0 : merged.totalTokens / activeDays;
  const observedInput = merged.uncachedInputTokens + merged.cachedInputTokens;
  const cachedShare = observedInput === 0 ? 0 : merged.cachedInputTokens / observedInput;

  return (
    <ScrollArea className="h-full">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-foreground">Usage</h1>
            <p className="text-sm text-muted-foreground">
              {subject === "me" ? "Your calls" : "Every member's calls"},{" "}
              {formatDayShort(window.sinceDay)} to {formatDayShort(window.untilDay)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="radiogroup"
              aria-label="Whose usage"
              className="flex overflow-hidden rounded-md border border-border"
            >
              {SUBJECT_OPTIONS.map((option) => (
                <button
                  key={option.subject}
                  type="button"
                  role="radio"
                  aria-checked={option.subject === subject}
                  onClick={() => setSubject(option.subject)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 text-xs",
                    option.subject === subject
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <option.icon className="size-3.5" aria-hidden="true" />
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex overflow-hidden rounded-md border border-border">
              {WINDOW_OPTIONS.map((option) => (
                <button
                  key={option.days}
                  type="button"
                  onClick={() => setWindowDays(option.days)}
                  className={cn(
                    "px-3 py-1.5 text-xs",
                    option.days === windowDays
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={refreshAll}
              aria-label="Refresh usage"
              className="rounded-md border border-border p-2 text-muted-foreground hover:text-foreground"
            >
              <RefreshCwIcon className="size-3.5" />
            </button>
          </div>
        </header>

        {subject === "me" && (
          <div className="border-y border-border/60 py-5">
            <PaidUsageLimit organizationId={organizationId} />
          </div>
        )}

        <UsageCoverageNotice
          environments={environments}
          duplicateSources={merged.duplicateSources}
          staleEnvironments={merged.staleEnvironments}
          coverageNotices={coverageNotices}
          isPartial={isPartial}
        />

        {isPending ? (
          <p className="py-16 text-center text-sm text-muted-foreground">Reading Amoreg usage…</p>
        ) : (
          <>
            {/* Cost first: the financial answer, then the tier split. */}
            <section className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
              {/* The summary follows the chart toggle, so the headline and the
                  series are always reading the same units. */}
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1">
                  <span className="text-xs tracking-wide text-muted-foreground uppercase">
                    {metric === "cost" ? "Model spend" : "Processed tokens"}
                  </span>
                  <span className="text-4xl font-semibold text-foreground tabular-nums">
                    {metric === "cost"
                      ? formatGbp(merged.costGbp)
                      : formatTokens(merged.totalTokens)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {metric === "cost"
                      ? describeExchangeRate(exchangeRate)
                      : `Input, cache reads and output across ${formatCount(merged.sessions)} conversations.`}
                  </span>
                </div>

                {orderedTiers.map((tier) => {
                  const share = metric === "cost" ? tier.costShare : tier.tokenShare;
                  return (
                    <div key={tier.tier} className="flex flex-col gap-1.5">
                      <div className="flex items-baseline justify-between">
                        <span className="flex items-center gap-2 text-sm text-foreground">
                          <TierSwatch tier={tier.tier} className="size-3" />
                          {TIER_LABEL[tier.tier]}
                        </span>
                        <span className="text-sm text-foreground tabular-nums">
                          {metric === "cost"
                            ? formatGbp(tier.costGbp)
                            : formatTokens(tier.totalTokens)}
                        </span>
                      </div>
                      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full"
                          style={{
                            width: `${(share * 100).toFixed(1)}%`,
                            backgroundColor: TIER_COLOR[tier.tier],
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {metric === "cost"
                          ? `${formatPercent(share)} of cost · ${formatTokens(tier.totalTokens)} tokens`
                          : `${formatPercent(share)} of tokens · ${formatGbp(tier.costGbp)}`}
                      </span>
                    </div>
                  );
                })}

              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-sm font-medium text-foreground">
                    Daily {metric === "tokens" ? "processed tokens" : "cost"}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="flex overflow-hidden rounded-md border border-border">
                      {(["cost", "tokens"] as const).map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setMetric(option)}
                          className={cn(
                            "px-2.5 py-1 text-[10px] tracking-wide uppercase",
                            option === metric
                              ? "bg-muted text-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    <UsageChartLegend tiers={tiers} />
                  </div>
                </div>
                <UsageTierChart days={days} daily={merged.daily} metric={metric} tiers={tiers} />
              </div>
            </section>

            <section className="grid grid-cols-2 gap-px border-y border-border bg-border md:grid-cols-5">
              <Metric
                label="Processed tokens"
                value={formatTokens(merged.totalTokens)}
                detail={`${formatTokens(dailyAverage)} per active day`}
              />
              <Metric
                label="Cached input"
                value={formatTokens(merged.cachedInputTokens)}
                detail={`${formatPercent(cachedShare)} of observed input`}
              />
              <Metric
                label="Uncached input"
                value={formatTokens(merged.uncachedInputTokens)}
                detail={`${formatTokens(merged.cacheCreationTokens)} cache writes`}
              />
              <Metric
                label="Output"
                value={formatTokens(merged.outputTokens)}
                detail={`includes ${formatTokens(merged.reasoningTokens)} reasoning`}
              />
              <Metric
                label="Cache savings"
                value={formatGbp(merged.costQuality.cacheSavingsGbp)}
                detail={
                  merged.costGbp > 0
                    ? `${(merged.costQuality.cacheSavingsGbp / merged.costGbp).toFixed(1)}x the spend`
                    : "vs full input rates"
                }
              />
            </section>

            <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-sm font-medium text-foreground">Breakdown</h2>
                  <div className="flex overflow-hidden rounded-md border border-border">
                    {(["tier", "day", "agent"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setBreakdown(option)}
                        className={cn(
                          "px-2.5 py-1 text-[10px] tracking-wide uppercase",
                          option === breakdown
                            ? "bg-muted text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {breakdown === "tier" ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 font-normal">Tier</th>
                        <th className="py-2 text-right font-normal">Cost</th>
                        <th className="py-2 text-right font-normal">Share</th>
                        <th className="py-2 text-right font-normal">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderedTiers.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-6 text-center text-muted-foreground">
                            No activity in this window.
                          </td>
                        </tr>
                      ) : (
                        orderedTiers.map((tier) => (
                          <tr key={tier.tier} className="border-b border-border/50">
                            <td className="py-2 text-foreground">
                              <span className="flex items-center gap-2">
                                <TierSwatch tier={tier.tier} className="size-2.5" />
                                {TIER_LABEL[tier.tier]}
                              </span>
                            </td>
                            <td className="py-2 text-right text-foreground tabular-nums">
                              {formatGbp(tier.costGbp)}
                            </td>
                            <td className="py-2 text-right text-muted-foreground tabular-nums">
                              {formatPercent(tier.costShare)}
                            </td>
                            <td className="py-2 text-right text-muted-foreground tabular-nums">
                              {formatTokens(tier.totalTokens)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                ) : breakdown === "agent" ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 font-normal">Agent</th>
                        <th className="py-2 text-right font-normal">Cost</th>
                        <th className="py-2 text-right font-normal">Share</th>
                        <th className="py-2 text-right font-normal">Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {merged.agents.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-6 text-center text-muted-foreground">
                            No activity in this window.
                          </td>
                        </tr>
                      ) : (
                        merged.agents.map((agent) => (
                          <tr key={agent.agent ?? ""} className="border-b border-border/50">
                            <td className="py-2 text-foreground">{agentLabel(agent.agent)}</td>
                            <td className="py-2 text-right text-foreground tabular-nums">
                              {formatGbp(agent.costGbp)}
                            </td>
                            <td className="py-2 text-right text-muted-foreground tabular-nums">
                              {formatPercent(agent.costShare)}
                            </td>
                            <td className="py-2 text-right text-muted-foreground tabular-nums">
                              {formatCount(agent.records)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="py-2 font-normal">Day</th>
                        {tiers.map((tier) => (
                          <th key={tier} className="py-2 text-right font-normal">
                            {TIER_LABEL[tier]}
                          </th>
                        ))}
                        <th className="py-2 text-right font-normal">Total</th>
                        <th className="py-2 text-right font-normal">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentDays.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tiers.length + 3}
                            className="py-6 text-center text-muted-foreground"
                          >
                            No activity in this window.
                          </td>
                        </tr>
                      ) : (
                        recentDays.map((day) => (
                          <tr key={day.day} className="border-b border-border/50">
                            <td className="py-2 text-foreground">{formatDayShort(day.day)}</td>
                            {tiers.map((tier) => (
                              <td
                                key={tier}
                                className="py-2 text-right text-muted-foreground tabular-nums"
                              >
                                {formatGbp(day.byTier.get(tier)?.costGbp ?? 0)}
                              </td>
                            ))}
                            <td className="py-2 text-right text-foreground tabular-nums">
                              {formatGbp(day.costGbp)}
                            </td>
                            <td className="py-2 text-right text-muted-foreground tabular-nums">
                              {formatTokens(day.totalTokens)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <h2 className="text-sm font-medium text-foreground">Cost quality</h2>
                <dl className="flex flex-col">
                  <QualityRow
                    label="Gateway billed"
                    value={formatPercent(merged.costQuality.gatewayBilledShare)}
                  />
                  <QualityRow
                    label="Rate-table priced"
                    value={formatPercent(merged.costQuality.modelPricedShare)}
                  />
                  <QualityRow
                    label="Unpriced"
                    value={formatPercent(merged.costQuality.unpricedShare)}
                  />
                  <QualityRow
                    label="Cache savings"
                    value={formatGbp(merged.costQuality.cacheSavingsGbp)}
                  />
                </dl>
              </div>
            </section>
          </>
        )}
      </div>
    </ScrollArea>
  );
}

/**
 * What the Agent breakdown calls each delegated helper (the subagent
 * directories under apps/agents/amoreg/agent/subagents/). A helper added
 * later shows under its own id until it gets a label here.
 */
const AGENT_LABEL: Readonly<Record<string, string>> = {
  browser: "Browser helper",
  worker: "Worker",
  worker_flash: "Worker (Ultrafast)",
  worker_pro: "Worker (Pro)",
  insight: "Insight research",
  collector: "Research collector",
};

function agentLabel(agent: string | null): string {
  if (agent === null) return "Main assistant";
  return AGENT_LABEL[agent] ?? agent;
}

/**
 * How the pounds on this page were arrived at.
 *
 * The rate is quoted, not assumed: a spend figure nobody can reconcile against
 * an invoice is decoration. When the feed could not be reached the sentence
 * says so rather than letting a fallback rate pass for a published one.
 */
function describeExchangeRate(exchangeRate: UsageExchangeRate | null): string {
  if (exchangeRate === null) return "Billed by the Amoreg gateway.";
  const rate = `£${exchangeRate.usdToGbp.toFixed(4)} to the dollar`;
  return exchangeRate.status === "unavailable"
    ? `Billed by the Amoreg gateway, converted at an unconfirmed ${rate}.`
    : `Billed by the Amoreg gateway, converted at ${rate}.`;
}

function Metric({
  label,
  value,
  detail,
}: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 bg-background px-4 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg text-foreground tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

function QualityRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground tabular-nums">{value}</dd>
    </div>
  );
}
